<template>
  
  <div 
    class   = "pt-3 pl-4 pr-2 item-container"
    :style  = "borderStyle"
    @click  = "onAnnotationClicked"
  >

    <Avatar :user="annotation.user"></Avatar>

    <!-- Text Bubble Triangle -->
    <div 
      class     = "triangle ml-2 mt-2"
      :class    = "{hidden: isTextBeingEdited}"
    >
    </div>

    <!-- Text -->
    <div class="text pr-2 d-flex">

      <!-- Text Entry -->
      <v-textarea
        class     = "text-body-2 flex-grow-1"
        v-if      = "isTextBeingEdited"
        v-model   = "textEntry"
        outlined
        autofocus
        dense
        auto-grow
        rows      = "1"
        hide-details
        @blur         = "onSaveChanges"
        @keydown.esc  = "onRejectChanges"
      >
      </v-textarea>

      <!-- Text Display -->
      <div 
        v-else
        class   = "pa-2 text-body-2 not-editing flex-grow-1"
        :class  = "{ editable: canEdit, description: displayTextIsDescription }"
        @click  = "onEdit"
      >
        {{ displayText }}
      </div>

    </div>

    <!-- Remove -->
    <v-btn
      icon
      :disabled   = "!canEdit"
      @click.stop = "onRemove"
    >
      <v-icon>mdi-comment-remove-outline</v-icon>
    </v-btn>

    <!-- Start / End -->
    <div class="time-container">

      <!-- Text Display -->
      <div
        v-if    = "!isTimeBeingEdited"
        class   = "pt-1 text-caption grey--text text--darken-1 time"
        @click  = "onEditTimes();"
      >
        {{ startLabel }} <span v-if="annotation.duration"><span class="mx-1">-</span>{{ endLabel }}</span>
      </div>  

      <div
        v-else
        class             = "pt-1 time"
        v-click-outside   = "() => isTimeBeingEdited = false"
        @keydown.esc      = "isTimeBeingEdited = false"
      >

        <!-- Start -->

        <!-- Frames -->
        <frame-input
          v-if      = "timeDisplaySource.type === 'frames'"
          ref       = "startField"
          class     = "frames"
          small
          v-model   = "startFrame"
        >
        </frame-input>

        <!-- Time -->
        <time-input
          v-if      = "timeDisplaySource.type === 'seconds'"
          ref       = "startField"
          small
          v-model   = "startTime"
        >
        </time-input>

        <!-- Timecode -->
        <timecode-input
          v-if      = "timeDisplaySource.type === 'timecode'"
          ref       = "startField"
          small
          v-model   = "startTimecode"
        >
        </timecode-input>

        <span class="mx-2">-</span>

        <!-- End -->

        <!-- Frames -->
        <frame-input
          v-if        = "timeDisplaySource.type === 'frames'"
          class       = "frames"
          small
          v-model     = "endFrame"
        >
        </frame-input>

        <!-- Time -->
        <time-input
          v-if      = "timeDisplaySource.type === 'seconds'"
          small
          v-model   = "endTime"
        >
        </time-input>

        <!-- Timecode -->
        <timecode-input
          v-if      = "timeDisplaySource.type === 'timecode'"
          small
          v-model   = "endTimecode"
        >
        </timecode-input>

      </div>

    </div>

    <!-- Replies -->
    <ReplyItem
      class         = "reply"
      v-for         = "reply of annotation.replies"
      :key          = "reply.id"
      :annotationId = "annotation.id"
      :reply        = "reply"
    >
    </ReplyItem>

    <v-btn
      class       = "ma-2 mb-0 reply-controls"
      x-small
      outlined
      color       = "primary"
      @click      = "onReply"
    >
      Reply
    </v-btn>

    <ConfirmationDialog ref="confirmation_dialog"></ConfirmationDialog>

  </div>

</template>

<script>

// #region Imports

import Vue from 'vue';

/* vuex */
import { mapState } from 'vuex';

/* Telestream */
import { AddTimecodes, SubtractTimecodes, ToFrames, ToSeconds, ToTimeCode } from '@user-interface/timecode-utilities';
import Avatar from './Avatar';
import ConfirmationDialog from '../dialogs/ConfirmationDialog';
import ReplyItem from './ReplyItem.vue';
import { SecondsToLongTime } from '@/utils/timeline-utilities';
import {
  ADD_REPLY_TO_ANNOTATION,
  JUMP_TO_FRAME, 
  REMOVE_ANNOTATION,
  UPDATE_ANNOTATION,
  PAUSE_VIDEO
} from '@/utils/action-types';
import {
  SET_ANNOTATION_TOOLS_COLOR,
  SET_ANNOTATION_BEING_EDITED
} from '@/utils/mutation-types';

// #endregion Imports

export default {

  // #region Properties

  props: {
    index:      Number,
    annotation: Object
  },
  data() {
    return {
      textEntry:         '',
      isTimeBeingEdited: false
    };
  },
  computed: {
    ...mapState([
      'video',
      'ui',
      'user',
      'timeDisplaySource'
    ]),
    borderStyle() {
      return {
        'border-left': `4px solid ${this.annotation.color}`
      };
    },
    startLabel() {
      switch (this.timeDisplaySource.type) {

      case 'frames': return this.annotation.frame;
      case 'seconds': return SecondsToLongTime(this.startTime);
      default:
      case 'timecode': return this.startTimecode.split('@')[0];

      }
    },
    endLabel() {
      switch (this.timeDisplaySource.type) {

      case 'frames': return this.annotation.frame + this.annotation.duration;
      case 'seconds': return SecondsToLongTime(this.endTime);
      default:
      case 'timecode': return this.endTimecode.split('@')[0];

      }
    },
    startFrame: {
      get() {
        return this.annotation.frame.toString();
      },
      set(frames) {
        const updatedAnnotation = {
          ...this.annotation,
          frame: frames
        }

        this.$store.dispatch(UPDATE_ANNOTATION, updatedAnnotation);
      }
    },
    endFrame: {
      get() {
        return (this.annotation.frame + this.annotation.duration).toString();
      },
      set(frames) {
        const duration = frames - this.annotation.frame;

        const updatedAnnotation = {
          ...this.annotation,
          duration: duration
        }

        this.$store.dispatch(UPDATE_ANNOTATION, updatedAnnotation);
      }
    },
    startTime: {
      get() {
        return ToSeconds(this.startTimecode, true);
      },
      set(time) {
        this.startFrame = Math.floor(time * this.video.framerate);
      }
    },
    endTime: {
      get() {
        return ToSeconds(this.endTimecode, true);
      },
      set(time) {
        this.endFrame = Math.floor(time * this.video.framerate);
      }
    },
    startTimecode: {
      get() {
        const annotationTimecode = ToTimeCode(this.annotation.frame, this.video.framerate, this.video.firstTimecode.includes(';'));
        return AddTimecodes(this.video.firstTimecode, annotationTimecode);
      },
      set(timecode) {
        const zeroBasedTimecode = SubtractTimecodes(timecode, this.video.firstTimecode);
        this.startFrame = ToFrames(zeroBasedTimecode);
      }
    },
    endTimecode: {
      get() {
        const annotationTimecode = ToTimeCode(this.annotation.frame + this.annotation.duration, this.video.framerate, this.video.firstTimecode.includes(';'));
        return AddTimecodes(this.video.firstTimecode, annotationTimecode);
      },
      set(timecode) {
        const zeroBasedTimecode = SubtractTimecodes(timecode, this.video.firstTimecode);
        this.endFrame = ToFrames(zeroBasedTimecode);
      }
    },
    canEdit() {
      return this.annotation.user === this.user.name;
    },
    isTextBeingEdited() {
      return (this.ui.annotationBeingEdited === this.annotation.id);
    },
    displayText() {
      if (this.annotation?.text) {
        return this.annotation.text;
      }

      if (this.annotation?.shapes?.length > 0) {
        return 'Added some doodles...';
      }

      return '';
    },
    displayTextIsDescription() {
      return (
        (!this.annotation?.text) &&
        (this.annotation?.shapes.length > 0)
      );
    }
  },
  watch: {
    annotation() {
      this.textEntry = this.annotation.text;
    }
  },
  components: {
    Avatar,
    ConfirmationDialog,
    ReplyItem
  },

  // #endregion Properties

  // #region Methods

  methods: {

    // #region Events

    onAnnotationClicked() {
      this.$store.dispatch(JUMP_TO_FRAME, this.annotation.frame);
      this.$store.dispatch(PAUSE_VIDEO);
      this.$store.commit(SET_ANNOTATION_TOOLS_COLOR, this.annotation.color);
    },

    onEdit() {
      if (!this.canEdit) {
        return;
      }

      this.textEntry = this.annotation.text;
      this.$store.commit(SET_ANNOTATION_BEING_EDITED, this.annotation.id);
    },

    onSaveChanges() {
      const updatedAnnotation = {
        ...this.annotation,
        text: this.textEntry
      };

      this.$store.dispatch(UPDATE_ANNOTATION, updatedAnnotation);
      this.$store.commit(SET_ANNOTATION_BEING_EDITED, undefined);
    },

    onRejectChanges() {
      this.textEntry = this.annotation.text;
      this.$store.commit(SET_ANNOTATION_BEING_EDITED, undefined);
    },

    onEditTimes() {
      this.isTimeBeingEdited = true;
      Vue.nextTick(() => {
        // TODO: Should add a focus method to the component.
        this.$refs.startField.$refs.hiddenInput.focus();
      });
    },

    async onRemove() {
      if (await this.$refs.confirmation_dialog.confirm(
        'Remove Annotation?',
        'Are you sure you want remove this annotation?',
        'Remove Annotation',
        'error',
        'Keep Annotation',
        'primary'
      )) {
        this.$store.dispatch(REMOVE_ANNOTATION, this.annotation);
      }
    },

    // #region Reply

    onReply() {
      this.$store.dispatch(ADD_REPLY_TO_ANNOTATION, this.annotation.id);
    }

    // #endregion Reply

    // #endregion Events

  }

  // #endregion Methods

}

</script>

<style lang="scss" scoped>

  .item-container {
    display:      grid;
    grid-template-columns: auto auto 1fr auto auto;

    .text {
      grid-column-start:    3;
      grid-column-end:      5;

      .not-editing {
        background-color:   rgba(255, 255, 255, 0.15);
        border-radius:      4px;
        white-space:        pre-line;
        word-break:         break-word;

        &.editable {
          cursor:             pointer;
        }

        &.description {
          font-style:         italic;
        }
      }
    }

    .time-container {
      grid-column-start:    3;
      
      .time {
        cursor:     pointer;
        display:    flex;

        .frames {
          width:    100px;
        }
      }
    }

    .reply {
      grid-column-start:    3;
      grid-column-end:      6;
    }

    .reply-controls {
      grid-column-start:    4;
    }
  }

  $triangle-size: 9px;

  .triangle {
    width: 0; 
    height: 0; 
    border-top:     $triangle-size solid transparent;
    border-bottom:  $triangle-size solid transparent; 
    
    border-right:   $triangle-size solid rgba(255, 255, 255, 0.15); 

    &.hidden {
      border-right-color: transparent;
    }
  }

</style>